import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions, RefreshControl, ActivityIndicator, FlatList, TouchableOpacity, Animated } from 'react-native';
import { Card, Icon } from 'react-native-elements';
import { LineChart } from 'react-native-chart-kit';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useNavigation } from '@react-navigation/native';
import type { UserProgress } from '../types/databaseTypes';

type ChartData = {
  labels: string[];
  accuracy: number[];
  difficulty: string[];
  grade: string[];
};

const SessionCard = ({ session, isExpanded, onPress }) => {
  const [animation] = useState(new Animated.Value(0));

  useEffect(() => {
    Animated.timing(animation, {
      toValue: isExpanded ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isExpanded]);

  const maxHeight = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [80, 400], // Adjust max height as needed
  });

  return (
    <TouchableOpacity onPress={onPress}>
      <Animated.View style={[styles.sessionCard, { maxHeight }]}>
        <View style={styles.sessionHeader}>
          <Text style={styles.sessionDate}>
            {new Date(session.date).toLocaleDateString()}
          </Text>
          <Text style={styles.sessionScore}>
            Accuracy: {session.session_score}%
          </Text>
          <Icon
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            type="feather"
            size={20}
          />
        </View>
        
        {isExpanded && (
          <View style={styles.sessionDetails}>
            <Text style={styles.detailText}>Grade: {session.grade}</Text>
            <Text style={styles.detailText}>Difficulty: {session.difficulty}</Text>
            <Text style={styles.detailText}>Total Words: {session.total_words}</Text>
            <Text style={styles.detailText}>Total Attempts: {session.total_attempts}</Text>
            
            {session.missed_words?.length > 0 && (
              <View style={styles.missedWordsSection}>
                <Text style={styles.sectionTitle}>Missed Words:</Text>
                <Text style={styles.wordsList}>
                  {session.missed_words.join(', ')}
                </Text>
              </View>
            )}
            
            <View style={styles.attemptsSection}>
              <Text style={styles.sectionTitle}>Attempts Per Word:</Text>
              {Object.entries(session.attempts_per_word || {}).map(([word, attempts]) => (
                <Text key={word} style={styles.attemptItem}>
                  {word}: {String(attempts)} attempts
                </Text>
              ))}
            </View>
          </View>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

export default function ProgressScreen() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<UserProgress[]>([]);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    accuracy: [],
    difficulty: [],
    grade: [],
  });
  const navigation = useNavigation();

  const processChartData = (sessions: UserProgress[]) => {
    // Filter out any sessions without valid data
    const validSessions = sessions.filter(s => 
      typeof s.correct_words === 'number' && 
      typeof s.total_words === 'number' &&
      s.total_words > 0
    );

    const last10Sessions = validSessions.slice(0, 10).reverse();
    const data: ChartData = {
      labels: last10Sessions.map((_, i) => `${i + 1}`),
      accuracy: last10Sessions.map(s => (s.correct_words / s.total_words) * 100),
      difficulty: last10Sessions.map(s => s.difficulty),
      grade: last10Sessions.map(s => s.grade || 'Unknown'),
    };
    console.log('Processed chart data:', data);
    setChartData(data);
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (user?.id) {
        fetchSessions();
      }
    });

    return unsubscribe;
  }, [navigation, user]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', user?.id)
        .eq('session_type', 'training')
        .order('date', { ascending: false })
        .limit(20)
        .throwOnError();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      console.log('Fetched sessions:', data);
      setSessions(data || []);
      processChartData(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error.message || error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const renderPerformanceGraph = () => {
    const screenWidth = Dimensions.get('window').width;
    
    if (!chartData.accuracy.length) {
      return (
        <Card containerStyle={styles.graphCard}>
          <Text style={styles.graphTitle}>No session data available yet</Text>
        </Card>
      );
    }

    return (
      <Card containerStyle={styles.graphCard}>
        <Text style={styles.graphTitle}>Accuracy Trend (Last 10 Sessions)</Text>
        <LineChart
          data={{
            labels: chartData.labels,
            datasets: [
              {
                data: chartData.accuracy.map(val => 
                  isNaN(val) ? 0 : Math.min(Math.max(val, 0), 100)
                ),
                color: (opacity = 1) => `rgba(76, 175, 80, ${opacity})`,
                strokeWidth: 2,
              },
            ],
          }}
          width={screenWidth - 40}
          height={220}
          chartConfig={{
            backgroundColor: '#ffffff',
            backgroundGradientFrom: '#ffffff',
            backgroundGradientTo: '#ffffff',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(76, 175, 80, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
            style: {
              borderRadius: 16,
            },
            propsForDots: {
              r: '6',
              strokeWidth: '2',
              stroke: '#4CAF50',
            },
          }}
          bezier
          style={styles.chart}
        />
        <View style={styles.legendContainer}>
          {chartData.labels.map((label, index) => (
            <View key={index} style={styles.legendItem}>
              <Text style={styles.legendText}>
                {`Session ${label}: ${chartData.grade[index].replace('grade', 'Grade ')} (${
                  chartData.difficulty[index]
                })`}
              </Text>
              <Text style={styles.accuracyText}>
                {`${Math.round(chartData.accuracy[index])}% accuracy`}
              </Text>
            </View>
          ))}
        </View>
      </Card>
    );
  };

  if (!user || user.id === 'guest') {
    return (
      <View style={styles.container}>
        <Card>
          <Text style={styles.guestMessage}>
            Please sign in to track your progress!
          </Text>
        </Card>
      </View>
    );
  }

  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={fetchSessions}
        />
      }
    >
      <Text style={styles.title}>Your Progress</Text>

      {renderPerformanceGraph()}

      {loading ? (
        <ActivityIndicator size="large" color="#007AFF" />
      ) : sessions.length > 0 ? (
        <View style={styles.sessionsContainer}>
          {sessions.map(session => (
            <SessionCard
              key={session.id}
              session={session}
              isExpanded={expandedSessionId === session.id}
              onPress={() => setExpandedSessionId(
                expandedSessionId === session.id ? null : session.id
              )}
            />
          ))}
        </View>
      ) : (
        <Text style={styles.guestMessage}>No sessions found.</Text>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 20,
    color: '#4CAF50',
  },
  graphCard: {
    borderRadius: 10,
    marginBottom: 15,
    elevation: 3,
  },
  graphTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    color: '#4CAF50',
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  sessionsContainer: {
    padding: 15,
  },
  sessionCard: {
    backgroundColor: 'white',
    borderRadius: 10,
    marginBottom: 10,
    padding: 15,
    elevation: 2,
    overflow: 'hidden',
  },
  sessionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sessionDate: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#666',
  },
  sessionScore: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  sessionDetails: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  detailText: {
    fontSize: 14,
    marginBottom: 5,
    color: '#666',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 10,
    color: '#4CAF50',
  },
  missedWordsSection: {
    marginTop: 10,
  },
  wordsList: {
    color: '#666',
    lineHeight: 20,
  },
  attemptsSection: {
    marginTop: 10,
  },
  attemptItem: {
    color: '#666',
    marginBottom: 3,
  },
  legendContainer: {
    marginTop: 15,
    paddingHorizontal: 10,
  },
  legendItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  legendText: {
    fontSize: 14,
    color: '#666',
  },
  accuracyText: {
    fontSize: 14,
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  guestMessage: {
    fontSize: 18,
    textAlign: 'center',
    color: '#666',
    padding: 20,
  },
}); 